"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { format, addMinutes } from "date-fns"
import { TimePickerDemo } from "@/components/forms/time-picker"
import { useComponentsTranslations, useCalendarTranslations, useCommonTranslations } from "@/hooks/use-translations"
import { fromLocalDateTimeInputValue, getDateLocale, toLocalDateTimeInputValue } from "@/lib/date-utils"
import { useLocale } from "@/lib/i18n-client"
import { Clock, Calendar as CalendarIcon, CheckCircle, X } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Input } from "@/components/ui/input"
import { TaskReminderSelect } from "@/components/task/task-reminder-select"
import { TaskDeadlineField } from "@/components/task/task-deadline-field"
// Popover removed for end-date; using custom overlay panel

interface ScheduleTaskModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: {
    id: string | number
    title: string
    duration: number
    // Optional fields for prefill
    scheduled_date?: string | null
    scheduled_time_utc?: string | null
    deadline_at?: string | null
    reminder_time?: number | null
    recurrence_enabled?: boolean
    recurrence_type?: 'daily' | 'weekly' | 'monthly'
    recurrence_interval?: number | null
    recurrence_weekdays?: number[] | null
    recurrence_anchor?: 'scheduled' | 'completion' | null
    recurrence_end_date?: string | null
  }
  onSchedule: (taskId: string, date: Date, recurrence?: {
    recurrence_enabled: boolean
    recurrence_type?: 'daily' | 'weekly' | 'monthly'
    recurrence_interval?: number | null
    recurrence_weekdays?: number[] | null
    recurrence_anchor?: 'scheduled' | 'completion' | null
    recurrence_end_date?: string | null
    reminder_time?: number | null
    deadline_at?: string | null
  }) => void
}

export function ScheduleTaskModal({ open, onOpenChange, task, onSchedule }: ScheduleTaskModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date())
  const [selectedTime, setSelectedTime] = useState<Date | undefined>(new Date())
  const [endTime, setEndTime] = useState<Date | undefined>(new Date())
  const [repeatEnabled, setRepeatEnabled] = useState(false)
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly')
  const [interval, setInterval] = useState<number>(1)
  const [weekdays, setWeekdays] = useState<number[]>([]) // 0=Sun..6=Sat
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [reminderTime, setReminderTime] = useState<number | null>(task.reminder_time ?? null)
  const [deadlineInput, setDeadlineInput] = useState(toLocalDateTimeInputValue(task.deadline_at))
  const [isEndDateOpen, setIsEndDateOpen] = useState(false)



  // Prefill modal from task values on open
  useEffect(() => {
    if (!open) return
    // Prefill time
    if (task.scheduled_time_utc) {
      const dt = new Date(task.scheduled_time_utc)
      setSelectedDate(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()))
      setSelectedTime(dt)
    } else if (task.scheduled_date) {
      setSelectedDate(new Date(task.scheduled_date + 'T00:00:00'))
    }

    // Prefill recurrence
    if (typeof task.recurrence_enabled === 'boolean') setRepeatEnabled(task.recurrence_enabled)
    if (task.recurrence_type) setFrequency(task.recurrence_type)
    if (typeof task.recurrence_interval === 'number') setInterval(Math.max(1, task.recurrence_interval))
    if (Array.isArray(task.recurrence_weekdays)) setWeekdays(task.recurrence_weekdays)
    if (task.recurrence_end_date) setEndDate(new Date(task.recurrence_end_date + 'T00:00:00'))
    setReminderTime(task.reminder_time ?? null)
    setDeadlineInput(toLocalDateTimeInputValue(task.deadline_at))
  }, [open, task])

  const tComponents = useComponentsTranslations()
  const tCalendar = useCalendarTranslations()
  const tCommon = useCommonTranslations()
  const locale = useLocale()

  // Update end time whenever task duration or start time changes
  useEffect(() => {
    if (selectedTime) {
      const calculatedEndTime = addMinutes(selectedTime, task.duration)
      setEndTime(calculatedEndTime)
    }
  }, [selectedTime, task.duration])

  const handleSchedule = () => {
    if (selectedDate && selectedTime) {
      // Validate end date if set
      if (endDate && selectedDate && endDate < selectedDate) {
        // Don't schedule if end date is before start date
        return
      }

      // Create a new date with the selected date and time
      const scheduledDateTime = new Date(selectedDate)

      // Set hours and minutes from the time picker
      scheduledDateTime.setHours(selectedTime.getHours())
      scheduledDateTime.setMinutes(selectedTime.getMinutes())
      scheduledDateTime.setSeconds(0)

      const recurrence = repeatEnabled
        ? {
            recurrence_enabled: true,
            recurrence_type: frequency,
            recurrence_interval: interval,
            recurrence_weekdays: frequency === 'weekly' ? weekdays : null,
            recurrence_anchor: 'scheduled' as const, // Always use 'scheduled' like Apple/Google Calendar
            recurrence_end_date: endDate ? endDate.toISOString().split('T')[0] : null,
            reminder_time: reminderTime,
            deadline_at: fromLocalDateTimeInputValue(deadlineInput),
          }
        : {
            recurrence_enabled: false,
            reminder_time: reminderTime,
            deadline_at: fromLocalDateTimeInputValue(deadlineInput),
          }

      onSchedule(task.id.toString(), scheduledDateTime, recurrence)
      onOpenChange(false)
    }
  }

  const isScheduleReady = selectedDate && selectedTime
  const hasEndDateValidationError = Boolean(endDate && selectedDate && endDate < selectedDate)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-6 space-y-6">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              {tComponents('schedule_task')}
            </DialogTitle>
            <DialogDescription className="text-base">
              {tComponents('choose_date_and_time', { taskTitle: task.title })}
            </DialogDescription>
          </DialogHeader>

          {/* Task Info Card */}
          <Card className="mt-6 bg-muted/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    {tComponents('task')}
                  </h3>
                  <p className="font-semibold text-lg mt-1 truncate" title={task.title}>
                    {task.title}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground ml-4">
                  <Clock className="h-4 w-4" />
                  <span>{task.duration} {tCalendar('minutes')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 space-y-6">
            {/* Date Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium flex items-center gap-2 mb-4">
                <CalendarIcon className="h-4 w-4" />
                {tCalendar('select_date')}
              </Label>
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-lg border shadow-sm"
                  locale={getDateLocale(locale)}
                  initialFocus
                />
              </div>
            </div>

            <Separator />

            {/* Time Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {tComponents('start_time')}
              </Label>
              <div className="flex justify-center">
                <TimePickerDemo date={selectedTime} setDate={setSelectedTime} />
              </div>
            </div>

            <Separator />

            <TaskReminderSelect value={reminderTime} onChange={setReminderTime} />

            <Separator />

            <TaskDeadlineField id="schedule-task-deadline" value={deadlineInput} onChange={setDeadlineInput} />

            <Separator />

            {/* Recurrence */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">{tComponents('repeat')}</Label>
                <Switch checked={repeatEnabled} onCheckedChange={setRepeatEnabled} />
              </div>

              {repeatEnabled && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{tComponents('frequency')}</Label>
                    <ToggleGroup type="single" value={frequency} onValueChange={(v) => v && setFrequency(v as 'daily' | 'weekly' | 'monthly')}>
                      <ToggleGroupItem value="daily">{tComponents('daily')}</ToggleGroupItem>
                      <ToggleGroupItem value="weekly">{tComponents('weekly')}</ToggleGroupItem>
                      <ToggleGroupItem value="monthly">{tComponents('monthly')}</ToggleGroupItem>
                    </ToggleGroup>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{tComponents('every')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        value={interval}
                        onChange={(e) => setInterval(Math.max(1, Number(e.target.value)))}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">
                        {frequency === 'daily' ? tComponents('days') : frequency === 'weekly' ? tComponents('weeks') : tComponents('months')}
                      </span>
                    </div>
                  </div>

                  {frequency === 'weekly' && (
                    <div className="space-y-3 md:col-span-2">
                      <Label className="text-sm font-medium">{tComponents('on_days')}</Label>
                      <div className="flex flex-wrap gap-2">
                        <ToggleGroup type="multiple" value={weekdays.map(String)} onValueChange={(vals) => setWeekdays(vals.map(v => Number(v)))} className="flex flex-wrap gap-2">
                          <ToggleGroupItem value="0" className="px-3 py-2 text-sm" title={tCalendar('sunday')}>
                            {tCalendar('sunday')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="1" className="px-3 py-2 text-sm" title={tCalendar('monday')}>
                            {tCalendar('monday')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="2" className="px-3 py-2 text-sm" title={tCalendar('tuesday')}>
                            {tCalendar('tuesday')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="3" className="px-3 py-2 text-sm" title={tCalendar('wednesday')}>
                            {tCalendar('wednesday')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="4" className="px-3 py-2 text-sm" title={tCalendar('thursday')}>
                            {tCalendar('thursday')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="5" className="px-3 py-2 text-sm" title={tCalendar('friday')}>
                            {tCalendar('friday')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="6" className="px-3 py-2 text-sm" title={tCalendar('saturday')}>
                            {tCalendar('saturday')}
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 md:col-span-2 relative">
                    <Label className="text-sm font-medium">{tComponents('end_date')} (optional)</Label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Button 
                          variant="outline" 
                          className={`w-full justify-start text-left font-normal ${hasEndDateValidationError ? 'border-red-500' : ''} ${!endDate ? 'text-muted-foreground' : ''}`}
                          type="button"
                          onClick={() => setIsEndDateOpen(true)}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "PPP", { locale: getDateLocale(locale) }) : tCommon('select_end_date')}
                        </Button>

                        {isEndDateOpen && (
                          <>
                            {/* Click-away overlay to close */}
                            <div
                              className="fixed inset-0 bg-background/40 backdrop-blur-[2px] z-[90]"
                              onClick={() => setIsEndDateOpen(false)}
                            />
                            {/* Anchored panel under the trigger */}
                            <div className="absolute z-[100] left-0 top-full mt-2">
                              <div className="rounded-lg border bg-background shadow-xl p-3">
                                <Calendar
                                  mode="single"
                                  selected={endDate ?? undefined}
                                  onSelect={(d) => setEndDate(d ?? null)}
                                  locale={getDateLocale(locale)}
                                  disabled={(date) => selectedDate ? date < selectedDate : false}
                                  initialFocus
                                />
                                <div className="mt-3 flex items-center justify-end gap-2">
                                  <Button variant="outline" onClick={() => setIsEndDateOpen(false)}>{tCommon('cancel')}</Button>
                                  <Button onClick={() => setIsEndDateOpen(false)}>{tCommon('save')}</Button>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      {endDate && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => { setEndDate(null); setIsEndDateOpen(false) }}
                          className="flex-shrink-0"
                          title="Clear end date"
                          type="button"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {/* Note: overlay panel is anchored to the trigger above */}
                    {hasEndDateValidationError && (
                      <p className="text-sm text-red-600 mt-1">
                        End date cannot be before the start date
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Schedule Summary */}
            {isScheduleReady && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-primary">
                        {tComponents('schedule_summary')}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">{tCalendar('date')}: </span>
                          <span className="font-medium">
                            {format(selectedDate, "PPP", { locale: getDateLocale(locale) })}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{tComponents('start_time')}: </span>
                          <span className="font-medium">
                            {format(selectedTime, "HH:mm", { locale: getDateLocale(locale) })}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{tCalendar('duration')}: </span>
                          <span className="font-medium">{task.duration} {tCalendar('minutes')}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{tCalendar('ends_at')}: </span>
                          <span className="font-medium">
                            {endTime ? format(endTime, "HH:mm", { locale: getDateLocale(locale) }) : "..."}
                          </span>
                        </div>
                        {deadlineInput && (
                          <div>
                            <span className="text-muted-foreground">Deadline: </span>
                            <span className="font-medium">
                              {format(new Date(deadlineInput), "PPP HH:mm", { locale: getDateLocale(locale) })}
                            </span>
                          </div>
                        )}
                        {repeatEnabled && (
                          <>
                            <div>
                              <span className="text-muted-foreground">{tComponents('repeat')}: </span>
                              <span className="font-medium">
                                {tComponents(frequency)} ({interval} {frequency === 'daily' ? tComponents('days') : frequency === 'weekly' ? tComponents('weeks') : tComponents('months')})
                              </span>
                            </div>
                            {endDate && (
                              <div>
                                <span className="text-muted-foreground">{tComponents('end_date')}: </span>
                                <span className="font-medium">
                                  {format(endDate, "PPP", { locale: getDateLocale(locale) })}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 bg-muted/30 border-t">
          <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto sm:justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="sm:w-auto"
            >
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={!isScheduleReady || hasEndDateValidationError}
              className="sm:w-auto"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              {!isScheduleReady
                ? tComponents('select_date_and_time_first')
                : hasEndDateValidationError
                ? 'Invalid end date'
                : tComponents('schedule_task')
              }
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
